<template>
    <div class="import-v2">
        <v-dialog :value="value" max-width="900px" persistent scrollable>
             <v-card class="rounded-lg">
                <v-card-title class="teal darken-4 white--text py-3">
                    Import lương nhân viên V2
                    <v-spacer></v-spacer>
                    <v-btn small icon dark @click="close"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                
                <v-card-text class="pt-4">
                    <v-form v-model="valid" id="form-v2" ref="form" @submit.prevent="saveData">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <month-picker width="100%" v-model="month" label="Chọn tháng" outlined dense></month-picker>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-file-input 
                                    v-model="file" 
                                    clearable 
                                    label="File Import Excel" 
                                    placeholder="Chọn file excel (.xlsx)" 
                                    accept=".xlsx" 
                                    outlined
                                    dense
                                    :loading="parsing"
                                    @change="onFileChange"
                                    :rules="[v => !!v || 'Vui lòng chọn file excel']"
                                ></v-file-input>
                            </v-col>
                        </v-row>
                    </v-form>

                    <!-- Hiển thị thông tin thống kê sau khi parse excel -->
                    <v-alert v-if="payload.length > 0" type="info" text dense class="mb-4">
                        Đã đọc file excel thành công. Phát hiện: <strong>{{ uniqueEmployees.length }}</strong> nhân viên và tổng cộng <strong>{{ payload.length }}</strong> bản ghi dữ liệu.
                    </v-alert>

                    <!-- Preview bảng dữ liệu ngang đầy đủ cột ban đầu của file Excel -->
                    <template v-if="previewData.length > 0">
                        <div class="subtitle-1 font-weight-bold mb-2">Xem trước dữ liệu (Tối đa 10 dòng đầu):</div>
                        <div style="overflow-x: auto; max-width: 100%;">
                            <v-data-table
                                :headers="previewHeaders"
                                :items="previewData"
                                hide-default-footer
                                dense
                                class="elevation-1 rounded text-no-wrap"
                                style="width: max-content; min-width: 100%;"
                            >
                            </v-data-table>
                        </div>
                    </template>
                    
                    <div class="mt-4">
                        <small class="red--text font-weight-bold">
                            Lưu ý: Dữ liệu lương v2 sau khi import sẽ được chuyển thành danh sách cấu trúc dynamic payload (Label, DataText, DataNumber, Type, EmployeeCode, ExcelAddress, Index).
                        </small>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="py-3">
                    <v-spacer></v-spacer>
                    <v-btn color="grey lighten-1" text @click="close" class="px-4">Hủy</v-btn>
                    <v-btn 
                        color="teal darken-4" 
                        dark 
                        form="form-v2" 
                        type="submit" 
                        :loading="loading" 
                        :disabled="!valid || payload.length === 0"
                        class="px-4 rounded"
                    >
                        Lưu Import V2
                    </v-btn>
                </v-card-actions>
             </v-card>
        </v-dialog>
    </div>
</template>

<script>
import http from '../../../components/services/http-common';
import EventBus from "../../../components/services/EventBus";
import moment from 'moment';

export default {
    name: "ImportV2",
    props: {
        // v-model binding để đóng/mở dialog
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            ROOT_URL: "employeeSalaryImports/",
            valid: false,
            month: moment().format("YYYY-MM"),
            file: null,
            parsing: false, // State khi đang đọc file excel
            loading: false, // State khi đang gọi API gửi dữ liệu lên server
            payload: [],    // Payload sau khi đã parse excel
            previewData: [], // Dữ liệu hiển thị preview các hàng của excel
            previewHeaders: [] // Tiêu đề các cột tương ứng trong file excel
        }
    },
    computed: {
        // Danh sách MSNV không bị trùng lặp
        uniqueEmployees() {
            const employees = this.payload.map(x => x.employeeCode);
            return [...new Set(employees)];
        }
    },
    methods: {
        // Toast thông báo thành công
        messageToast(eventObj) {
            EventBus.$emit('hasMessage', eventObj);
        },
        // Toast thông báo lỗi
        errorToast(eventObj) {
            EventBus.$emit('hasError', eventObj);
        },
        // Tải thư viện XLSX từ CDN để tránh phụ thuộc gói cài đặt
        loadXlsxScript() {
            return new Promise((resolve, reject) => {
                if (window.XLSX) {
                    resolve(window.XLSX);
                    return;
                }
                const script = document.createElement('script');
                script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
                script.onload = () => resolve(window.XLSX);
                script.onerror = (err) => reject(err);
                document.head.appendChild(script);
            });
        },
        // Đọc dữ liệu file dạng ArrayBuffer
        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (err) => reject(err);
                reader.readAsArrayBuffer(file);
            });
        },
        // Đóng dialog và reset form
        close() {
            this.$emit("input", false); // v-model sync
            this.$emit("CloseDialog");
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
            this.file = null;
            this.payload = [];
            this.previewData = [];
            this.previewHeaders = [];
            this.month = moment().format("YYYY-MM");
        },
        // Khi người dùng chọn file, tự động parse excel sang payload v2
        async onFileChange(file) {
            if (!file) {
                this.payload = [];
                this.previewData = [];
                this.previewHeaders = [];
                return;
            }
            this.parsing = true;
            try {
                // Đảm bảo thư viện excel đã tải xong
                await this.loadXlsxScript();
                const arrayBuffer = await this.readFileAsArrayBuffer(file);
                
                const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                
                // Parse sheet sang cấu trúc mong muốn
                this.payload = this.parseSheetToPayload(sheet);
                
                // Tạo tiêu đề cột và dòng preview dựa theo cấu trúc file Excel gốc
                this.generatePreview(sheet);
                
                this.messageToast({ message: `Đọc dữ liệu thành công! Tổng cộng có ${this.payload.length} ô dữ liệu được phân tích.`, toast: true });
            } catch (err) {
                console.error(err);
                this.errorToast({ message: "Lỗi phân tích file Excel: " + err.message, toast: true });
                this.file = null;
                this.payload = [];
                this.previewData = [];
                this.previewHeaders = [];
            } finally {
                this.parsing = false;
            }
        },
        // Tạo dữ liệu dạng bảng nguyên cột cho phần preview
        generatePreview(sheet) {
            const ref = sheet['!ref'];
            if (!ref) return;

            const range = window.XLSX.utils.decode_range(ref);
            const startRow = range.s.r;
            const endRow = range.e.r;
            const startCol = range.s.c;
            const endCol = range.e.c;

            // 1. Quét tìm dòng chứa số thứ tự cột (1, 2, 3...)
            let indexRowIndex = -1;
            let msnvColIndex = -1;
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol - 2; c++) {
                    const c1 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c })];
                    const c2 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c + 1 })];
                    const c3 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c + 2 })];
                    
                    if (c1 && c2 && c3 &&
                        (c1.v === 1 || String(c1.v).trim() === '1') &&
                        (c2.v === 2 || String(c2.v).trim() === '2') &&
                        (c3.v === 3 || String(c3.v).trim() === '3')) {
                        indexRowIndex = r;
                        break;
                    }
                }
                if (indexRowIndex !== -1) break;
            }

            let headerRowIndex = -1;
            let dataStartRow = -1;

            if (indexRowIndex === -1) {
                // Fallback: Tìm dòng tiêu đề chứa ô có tên "MSNV"
                for (let r = startRow; r <= endRow; r++) {
                    for (let c = startCol; c <= endCol; c++) {
                        const cellAddress = window.XLSX.utils.encode_cell({ r: r, c: c });
                        const cell = sheet[cellAddress];
                        if (cell && cell.v) {
                            const valStr = String(cell.v).trim().toUpperCase();
                            if (valStr === 'MSNV' || valStr === 'MÃ NS' || valStr === 'MÃ NV' || valStr === 'MÃ NHÂN VIÊN') {
                                headerRowIndex = r;
                                msnvColIndex = c;
                                break;
                            }
                        }
                    }
                    if (headerRowIndex !== -1) break;
                }

                if (headerRowIndex === -1) {
                    headerRowIndex = 2; // Giả định dòng 3
                    msnvColIndex = 0;   // Giả định cột A
                }
                dataStartRow = headerRowIndex + 1;
            } else {
                headerRowIndex = indexRowIndex - 1;
                dataStartRow = indexRowIndex + 1;

                // Tìm cột MSNV trên dòng tiêu đề
                for (let c = startCol; c <= endCol; c++) {
                    const cellAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex, c: c });
                    const cell = sheet[cellAddress];
                    if (cell && cell.v) {
                        const valStr = String(cell.v).trim().toUpperCase();
                        if (valStr === 'MSNV' || valStr === 'MÃ NS' || valStr === 'MÃ NV' || valStr === 'MÃ NHÂN VIÊN') {
                            msnvColIndex = c;
                            break;
                        }
                    }
                }

                // Fallback nếu không thấy tiêu đề chữ MSNV: tìm ô có index bằng 1
                if (msnvColIndex === -1) {
                    for (let c = startCol; c <= endCol; c++) {
                        const indexAddress = window.XLSX.utils.encode_cell({ r: indexRowIndex, c: c });
                        const indexCell = sheet[indexAddress];
                        if (indexCell && (indexCell.v === 1 || String(indexCell.v).trim() === '1')) {
                            msnvColIndex = c;
                            break;
                        }
                    }
                }

                if (msnvColIndex === -1) {
                    msnvColIndex = startCol;
                }
            }

            // 2. Map nhãn cột làm headers cho v-data-table preview (chỉ lấy cột có tiêu đề)
            const headers = [];
            const colIndexMap = {};
            const colIndexNumMap = {};

            for (let c = startCol; c <= endCol; c++) {
                const headerAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex, c: c });
                const headerCell = sheet[headerAddress];
                let label = headerCell && headerCell.v ? String(headerCell.v).trim() : '';

                // Nếu nhãn trống (do gộp ô dọc), kiểm tra dòng phía trên
                if (!label && headerRowIndex > 0) {
                    const aboveAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex - 1, c: c });
                    const aboveCell = sheet[aboveAddress];
                    label = aboveCell && aboveCell.v ? String(aboveCell.v).trim() : '';
                }

                // Làm sạch nhãn: loại bỏ xuống dòng và khoảng trắng dư thừa
                label = label.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
                
                // Chỉ nhận các cột thực sự có title
                if (label) {
                    colIndexMap[c] = label;
                    
                    // Lấy số chỉ mục cột (ví dụ: 1, 2, 55)
                    let colIndexVal = c + 1;
                    if (indexRowIndex !== -1) {
                        const indexAddress = window.XLSX.utils.encode_cell({ r: indexRowIndex, c: c });
                        const indexCell = sheet[indexAddress];
                        if (indexCell && indexCell.v && !isNaN(indexCell.v)) {
                            colIndexVal = Number(indexCell.v);
                        } else {
                            colIndexVal = 1000 + c;
                        }
                    } else {
                        colIndexVal = c + 1;
                    }
                    colIndexNumMap[c] = colIndexVal;
                }
            }

            // Xây dựng headers list dựa trên index thay vì nhãn chữ
            for (let c = startCol; c <= endCol; c++) {
                const label = colIndexMap[c];
                if (label) {
                    const colLetter = window.XLSX.utils.encode_col(c);
                    const colIndexVal = colIndexNumMap[c];
                    const headerDisplayText = `${label} [${colLetter} - ${colIndexVal}]`;
                    headers.push({
                        text: headerDisplayText,
                        value: String(colIndexVal),
                        sortable: false
                    });
                }
            }
            this.previewHeaders = headers;

            // 3. Lặp qua 10 dòng đầu để xem trước
            const previewRows = [];
            const maxPreviewRows = 10;
            let counted = 0;

            for (let r = dataStartRow; r <= endRow; r++) {
                const msnvAddress = window.XLSX.utils.encode_cell({ r: r, c: msnvColIndex });
                const msnvCell = sheet[msnvAddress];
                // Kiểm tra xem dòng đó có chứa MSNV thực sự hay không
                if (!msnvCell || msnvCell.v === undefined || msnvCell.v === null || String(msnvCell.v).trim() === '') {
                    continue;
                }

                const rowObj = {};
                for (let c = startCol; c <= endCol; c++) {
                    const label = colIndexMap[c];
                    // Bỏ qua cột nếu cột này không được đăng ký tiêu đề
                    if (!label) continue;

                    const colIndexVal = colIndexNumMap[c];
                    const cellAddress = window.XLSX.utils.encode_cell({ r: r, c: c });
                    const cell = sheet[cellAddress];
                    
                    if (cell && cell.v !== undefined && cell.v !== null) {
                        rowObj[String(colIndexVal)] = cell.w !== undefined ? String(cell.w).trim() : String(cell.v).trim();
                    } else {
                        rowObj[String(colIndexVal)] = '';
                    }
                }
                previewRows.push(rowObj);
                counted++;
                if (counted >= maxPreviewRows) break;
            }
            this.previewData = previewRows;
        },
        // Hàm phân tích Excel sheet sang array object v2
        parseSheetToPayload(sheet) {
            const payload = [];
            const ref = sheet['!ref'];
            if (!ref) return payload;

            const range = window.XLSX.utils.decode_range(ref);
            const startRow = range.s.r;
            const endRow = range.e.r;
            const startCol = range.s.c;
            const endCol = range.e.c;

            // 1. Quét tìm dòng chứa số thứ tự cột (1, 2, 3...)
            let indexRowIndex = -1;
            let msnvColIndex = -1;
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol - 2; c++) {
                    const c1 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c })];
                    const c2 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c + 1 })];
                    const c3 = sheet[window.XLSX.utils.encode_cell({ r: r, c: c + 2 })];
                    
                    if (c1 && c2 && c3 &&
                        (c1.v === 1 || String(c1.v).trim() === '1') &&
                        (c2.v === 2 || String(c2.v).trim() === '2') &&
                        (c3.v === 3 || String(c3.v).trim() === '3')) {
                        indexRowIndex = r;
                        break;
                    }
                }
                if (indexRowIndex !== -1) break;
            }

            let headerRowIndex = -1;
            let dataStartRow = -1;

            if (indexRowIndex === -1) {
                // Fallback: Tìm dòng tiêu đề chứa ô có tên "MSNV"
                for (let r = startRow; r <= endRow; r++) {
                    for (let c = startCol; c <= endCol; c++) {
                        const cellAddress = window.XLSX.utils.encode_cell({ r: r, c: c });
                        const cell = sheet[cellAddress];
                        if (cell && cell.v) {
                            const valStr = String(cell.v).trim().toUpperCase();
                            if (valStr === 'MSNV' || valStr === 'MÃ NS' || valStr === 'MÃ NV' || valStr === 'MÃ NHÂN VIÊN') {
                                headerRowIndex = r;
                                msnvColIndex = c;
                                break;
                            }
                        }
                    }
                    if (headerRowIndex !== -1) break;
                }

                if (headerRowIndex === -1) {
                    headerRowIndex = 2; // Giả định dòng 3
                    msnvColIndex = 0;   // Giả định cột A
                }
                dataStartRow = headerRowIndex + 1;
            } else {
                headerRowIndex = indexRowIndex - 1;
                dataStartRow = indexRowIndex + 1;

                // Tìm cột MSNV trên dòng tiêu đề
                for (let c = startCol; c <= endCol; c++) {
                    const cellAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex, c: c });
                    const cell = sheet[cellAddress];
                    if (cell && cell.v) {
                        const valStr = String(cell.v).trim().toUpperCase();
                        if (valStr === 'MSNV' || valStr === 'MÃ NS' || valStr === 'MÃ NV' || valStr === 'MÃ NHÂN VIÊN') {
                            msnvColIndex = c;
                            break;
                        }
                    }
                }

                // Fallback nếu không thấy tiêu đề chữ MSNV: tìm ô có index bằng 1
                if (msnvColIndex === -1) {
                    for (let c = startCol; c <= endCol; c++) {
                        const indexAddress = window.XLSX.utils.encode_cell({ r: indexRowIndex, c: c });
                        const indexCell = sheet[indexAddress];
                        if (indexCell && (indexCell.v === 1 || String(indexCell.v).trim() === '1')) {
                            msnvColIndex = c;
                            break;
                        }
                    }
                }

                if (msnvColIndex === -1) {
                    msnvColIndex = startCol;
                }
            }

            const colIndexMap = {}; // Nhãn cột
            const colIndexNumMap = {}; // Chỉ mục cột

            // 2. Map nhãn cột và chỉ mục cột (chỉ lấy cột có tiêu đề)
            for (let c = startCol; c <= endCol; c++) {
                const headerAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex, c: c });
                const headerCell = sheet[headerAddress];
                let label = headerCell && headerCell.v ? String(headerCell.v).trim() : '';

                // Nếu nhãn trống (do gộp ô dọc), kiểm tra dòng phía trên
                if (!label && headerRowIndex > 0) {
                    const aboveAddress = window.XLSX.utils.encode_cell({ r: headerRowIndex - 1, c: c });
                    const aboveCell = sheet[aboveAddress];
                    label = aboveCell && aboveCell.v ? String(aboveCell.v).trim() : '';
                }

                // Làm sạch nhãn: loại bỏ xuống dòng và khoảng trắng dư thừa
                label = label.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
                
                // Chỉ nhận các cột thực sự có title
                if (label) {
                    colIndexMap[c] = label;

                    // Đọc chỉ mục số cột từ dòng chỉ mục
                    if (indexRowIndex !== -1) {
                        const indexAddress = window.XLSX.utils.encode_cell({ r: indexRowIndex, c: c });
                        const indexCell = sheet[indexAddress];
                        if (indexCell && indexCell.v && !isNaN(indexCell.v)) {
                            colIndexNumMap[c] = Number(indexCell.v);
                        } else {
                            colIndexNumMap[c] = 1000 + c;
                        }
                    } else {
                        colIndexNumMap[c] = c + 1;
                    }
                }
            }

            // 3. Quét dữ liệu từng dòng
            for (let r = dataStartRow; r <= endRow; r++) {
                const msnvAddress = window.XLSX.utils.encode_cell({ r: r, c: msnvColIndex });
                const msnvCell = sheet[msnvAddress];
                if (!msnvCell || msnvCell.v === undefined || msnvCell.v === null || String(msnvCell.v).trim() === '') {
                    continue;
                }

                const employeeCode = String(msnvCell.v).trim();

                for (let c = startCol; c <= endCol; c++) {
                    const label = colIndexMap[c];
                    // Bỏ qua cột nếu cột này không có tiêu đề
                    if (!label) continue;

                    const cellAddress = window.XLSX.utils.encode_cell({ r: r, c: c });
                    const cell = sheet[cellAddress];

                    if (cell && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
                        const excelAddress = cellAddress;
                        const colIndexVal = colIndexNumMap[c];

                        let dataText = cell.w !== undefined ? String(cell.w).trim() : String(cell.v).trim();
                        let dataNumber = 0;
                        let type = 'Text';

                        if (cell.t === 'n') {
                            dataNumber = Number(cell.v);
                            type = 'Number';
                        } else if (cell.t === 'b') {
                            type = 'Boolean';
                        } else if (cell.t === 'd') {
                            type = 'Date';
                        } else {
                            const parsedNum = Number(cell.v);
                            if (!isNaN(parsedNum) && String(cell.v).trim() !== '') {
                                dataNumber = parsedNum;
                                type = 'Number';
                            }
                        }

                        payload.push({
                            label: label,
                            dataText: dataText,
                            dataNumber: dataNumber,
                            type: type,
                            employeeCode: employeeCode,
                            excelAddress: excelAddress,
                            index: colIndexVal
                        });
                    }
                }
            }

            return payload;
        },
        // Gọi API lưu payload lên server
        saveData() {
            if (this.payload.length === 0) return;
            this.loading = true;

            const JwtString = localStorage.getItem('CaBToken');
            
            // Gửi dữ liệu theo đúng cấu trúc SalaryImportV2Payload { Data: [...] } tới endpoint import/v2
            return http.post(this.ROOT_URL + "import/v2", { Data: this.payload }, {
                params: {
                    month: this.month,
                    jwtString: JwtString
                }
            })
            .then(res => {
                this.messageToast({ message: "Lưu dữ liệu Import V2 thành công!", toast: true });
                this.$emit("fetchData");
                this.close();
            })
            .catch(err => {
                console.error(err);
                if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                    localStorage.removeItem('CaBToken');
                    this.$emit("passwordDialog");
                }
                this.errorToast({ message: err.response?.data?.message || "Import V2 thất bại!", toast: true });
            })
            .finally(() => {
                this.loading = false;
            });
        }
    }
}
</script>

<style scoped>
/* Bo tròn mượt mà cho bảng */
.v-data-table {
    border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
